import {
  effectiveRoleKey,
  formatCount,
  roleDisplayName,
  roleLabel,
  tt,
  type RoleColor,
} from '@agos/shared'

import type {
  RoleAffectedPerson,
  RoleEdge,
  RoleListReach,
  RoleOption,
  RolePerson,
  RolePreview,
  RoleRefusedRight,
  RoleView,
  UpdateRoleRequest,
} from '../roles/roles-api'

/**
 * What the roles screen says about a role, a person and a proposed change - `FRONTEND.md` §9.752 (owner O119),
 * on `BACKEND.md` §10A.407 and §10A.408.
 *
 * **Pure, and that is the reason it is a file of its own.** The screen draws these sentences on cards, in the editor,
 * in the preview rail and in the person panel; spelling one of them inside a component is how two of those places
 * come to say the same thing two ways. Every figure here is the server's (`GET /settings/roles`) - this file only
 * says it.
 */

// ─── the tabs ────────────────────────────────────────────────────────────────

export type PermissionsTab = 'roles' | 'people' | 'holders' | 'delegation' | 'history'

/**
 * Who sees which tabs - design §2.9's table.
 *
 * **A delegate gets *Role* and *Ľudia* and nothing else**, and that is the server's rule before it is the screen's:
 * *Kto čo smie* needs everybody's exceptions, *Kto spravuje role* writes delegation and *História* reads the trail, and
 * all three are the main administrator's (§10A.408 - a delegate creates, renames, deletes nothing and reads no trail).
 * A tab the server would refuse is not offered, because a tab that opens on a refusal is §9.7's defect.
 */
export function tabsFor(mainAdmin: boolean): PermissionsTab[] {
  return mainAdmin ? ['roles', 'people', 'holders', 'delegation', 'history'] : ['roles', 'people']
}

/** The tab a `?tab=` names, if the reader has it - otherwise *Role*, the default. */
export function tabFrom(value: string | null | undefined, available: readonly PermissionsTab[]): PermissionsTab {
  const found = available.find((tab) => tab === value)
  return found ?? 'roles'
}

/** Where on the screen the reader is - `?tab=&role=&person=`. `''` and `0` are *none*. */
export interface Place {
  tab: PermissionsTab
  role: string
  person: number
}

export type Navigate = (
  next: { tab?: PermissionsTab; role?: string; person?: number },
  options?: { replace?: boolean },
) => void

// ─── the cards ───────────────────────────────────────────────────────────────

/**
 * The built-ins in the order the screen draws them: *Hlavný administrátor* read-only first, then the four editable
 * ones as the server seeds their `sort_order` (V89: 10, 20, 30, 40). Custom roles follow.
 */
export const BUILT_IN_ORDER: readonly string[] = ['main_admin', 'admin', 'cook', 'waiter', 'driver']

function builtInRank(key: string): number {
  const index = BUILT_IN_ORDER.indexOf(key)
  return index === -1 ? BUILT_IN_ORDER.length : index
}

/**
 * The cards' order: the built-ins by `BUILT_IN_ORDER`, then the custom roles by their `sortOrder`, then by name.
 *
 * **Ranked by the key rather than by `builtIn`**, because `main_admin` is a pseudo-role the server lists with
 * `builtIn` true and no `staff_roles` row: the five keys are the vocabulary, and a custom role cannot take one of
 * them (V89's `staff_roles_shape_check`).
 */
export function orderRoles(roles: readonly RoleView[]): RoleView[] {
  return [...roles].sort(
    (left, right) =>
      builtInRank(left.key) - builtInRank(right.key) ||
      left.sortOrder - right.sortOrder ||
      roleDisplayName(left).localeCompare(roleDisplayName(right), 'sk') ||
      left.key.localeCompare(right.key),
  )
}

/** A role's name on screen by its key, from the roles the reader may see; an unseen key by its built-in label. */
export function roleNameOf(roles: readonly RoleView[], key: string): string {
  const role = roles.find((candidate) => candidate.key === key)
  return role === undefined ? roleLabel(key) : roleDisplayName(role)
}

/** The people holding a role - `COALESCE(role_key, system_role)`, the rule the server decides by. */
export function membersOf(roleKey: string, people: readonly RolePerson[]): RolePerson[] {
  return people.filter((person) => effectiveRoleKey(person) === roleKey)
}

/** *nikto · 1 človek · 3 ľudia · 7 ľudí* - the design's own four forms. */
export function peopleCountText(count: number): string {
  if (count <= 0) return tt('nikto')
  if (count === 1) return tt('1 človek')
  if (count < 5) return `${formatCount(count)} ${tt('ľudia')}`
  return `${formatCount(count)} ${tt('ľudí')}`
}

/** *1 právo · 3 práva · 12 práv* - and *0 práv*, which is how a role nobody has ticked anything for reads. */
export function rightsCountText(count: number): string {
  if (count === 1) return tt('1 právo')
  if (count >= 2 && count <= 4) return `${formatCount(count)} ${tt('práva')}`
  return `${formatCount(count)} ${tt('práv')}`
}

/** *1 volanie · 3 volania · 235 volaní*, as the object of *otvára*. */
function callsWord(count: number): string {
  if (count === 1) return tt('volanie')
  if (count >= 2 && count <= 4) return tt('volania')
  return tt('volaní')
}

export interface RightsFigures {
  /** The role's rights the catalogue knows - a stale key on a row is not a right anybody can use. */
  rights: number
  /** How many of those the server checks at all (`serverChecked`); the rest only move the menu and the screen. */
  serverChecked: number
}

/**
 * *N práv · M z nich kontroluje server* - what a tick on this role is worth on the server.
 *
 * **Counted against the catalogue**, so a key the server no longer knows (a row a migration left) is neither a right
 * nor a checked one; the card would otherwise promise a right no screen can draw.
 */
export function rightsFigures(
  permissions: readonly string[],
  catalogue: readonly string[],
  serverChecked: readonly string[],
): RightsFigures {
  const known = new Set(catalogue)
  const checked = new Set(serverChecked)
  const held = [...new Set(permissions)].filter((key) => known.has(key))
  return { rights: held.length, serverChecked: held.filter((key) => checked.has(key)).length }
}

export function rightsFiguresText(figures: RightsFigures): string {
  return `${rightsCountText(figures.rights)} · ${formatCount(figures.serverChecked)} ${tt('z nich kontroluje server')}`
}

export type RoleWarningKind = 'administrator-pass' | 'admin-base'

export interface RoleWarning {
  kind: RoleWarningKind
  text: string
}

/**
 * The two traps, said only where they are true - design §2.9.
 *
 * - **The built-in Administrátor's pass** (owner question #58): the server lets it through on a right it has not
 *   ticked, unless that person is denied it personally. Only `administratorPass` says so, because only the built-in
 *   role has it - a custom role based on Administrátor is held to its ticks (§10A.407, #162).
 * - **What an Administrátor base opens without a tick**: the calls whose guard names the role rather than a right
 *   (`roleListReach.admin`). Every base has some, and the other three are said in the editor's base sentence; this
 *   one is a warning because it is the base a delegate may never hand out.
 *
 * A reach the server answered as nought is no warning, and an unknown reach still says such calls exist.
 */
export function roleWarnings(
  role: Pick<RoleView, 'key' | 'baseRole' | 'administratorPass'>,
  reach: RoleListReach | null | undefined,
): RoleWarning[] {
  const warnings: RoleWarning[] = []
  if (role.key === 'main_admin') return warnings
  // §9.764 - no warning about the built-in Administrátor's pass: the owner keeps it (#58 answered, *"Thats ok"*).
  if (role.baseRole === 'admin') {
    const calls = reach?.admin
    if (calls == null || !Number.isFinite(calls)) {
      warnings.push({
        kind: 'admin-base',
        text: tt('Základ Administrátor otvára aj volania, ktoré nemajú vlastné právo.'),
      })
    } else if (calls > 0) {
      const count = Math.floor(calls)
      warnings.push({
        kind: 'admin-base',
        text: `${tt('Základ Administrátor otvára aj')} ${formatCount(count)} ${callsWord(count)} ${tt('bez vlastného práva.')}`,
      })
    }
  }
  return warnings
}

// ─── delegation in words ─────────────────────────────────────────────────────

/** *upraviť, prideliť* - the two verbs of one edge, in the order the delegation tab draws them. */
export function verbsText(edge: { assign: boolean; edit: boolean }): string {
  if (edge.edit && edge.assign) return tt('upraviť, prideliť')
  if (edge.edit) return tt('upraviť')
  if (edge.assign) return tt('prideliť')
  return ''
}

/**
 * *Čašník (upraviť, prideliť), Barman (prideliť)* - a list of edges as a sentence, in the cards' order.
 *
 * An edge with neither verb says nothing and is left out: V89 refuses one (`does_something`), and drawing it would
 * read as a role managing another with no power over it.
 */
export function edgesText(edges: readonly RoleEdge[], roles: readonly RoleView[]): string {
  const rank = new Map(orderRoles(roles).map((role, index) => [role.key, index]))
  return edges
    .filter((edge) => edge.assign || edge.edit)
    .sort(
      (left, right) =>
        (rank.get(left.role) ?? Number.MAX_SAFE_INTEGER) - (rank.get(right.role) ?? Number.MAX_SAFE_INTEGER) ||
        left.role.localeCompare(right.role),
    )
    .map((edge) => `${roleNameOf(roles, edge.role)} (${verbsText(edge)})`)
    .join(', ')
}

/**
 * What the reader's own role manages - the delegate's action line, *Spravujete: …*.
 *
 * **Read from both directions.** The reader's own role is in their overview read-only, with its `manages`; each role
 * they manage also lists them in `managedBy`. Either alone is the answer when the server is consistent, and the union
 * is the answer when one half is missing - a sentence of what somebody manages must not go quiet because one list
 * was empty.
 */
export function edgesOfViewer(roles: readonly RoleView[], viewerKey: string): RoleEdge[] {
  const found = new Map<string, RoleEdge>()
  for (const edge of roles.find((role) => role.key === viewerKey)?.manages ?? []) {
    if (edge.assign || edge.edit) found.set(edge.role, edge)
  }
  for (const role of roles) {
    if (found.has(role.key)) continue
    const edge = role.managedBy.find((candidate) => candidate.role === viewerKey)
    if (edge !== undefined && (edge.assign || edge.edit)) {
      found.set(role.key, { role: role.key, assign: edge.assign, edit: edge.edit })
    }
  }
  return [...found.values()]
}

// ─── the editor ──────────────────────────────────────────────────────────────

/**
 * Whether the reader may change this role's rights.
 *
 * **The server's flag, or a main administrator's own reach** (§10A.408's R4): a main administrator may edit every
 * role but their own pseudo-role, and the overview's `you` says the same thing - so either is enough, and a flag the
 * server left false for a main administrator does not lock them out of a screen that is theirs. *Hlavný
 * administrátor* is never editable: it holds everything and has no rows to change.
 */
export function mayEditRights(role: Pick<RoleView, 'key' | 'you'>, viewerMainAdmin: boolean): boolean {
  if (role.key === 'main_admin') return false
  return role.you.mayEditPermissions || viewerMainAdmin
}

/** Why a delegate cannot touch one right - §10A.408's R8. */
export type RightLock = 'main-admin-only' | 'beyond-yours'

export interface LockContext {
  /** A main administrator has no locks at all. */
  mainAdmin: boolean
  /** What the reader may add (`you.grantable`): their own rights, less the main administrator's. */
  grantable: ReadonlySet<string>
  /** `mainAdminGrants` - the rights only a main administrator hands out, either way. */
  mainAdminGrants: ReadonlySet<string>
  /** The role's rights as stored; restoring one of them is not adding it. */
  stored: ReadonlySet<string>
}

/**
 * The lock on one switch for a delegate, or null.
 *
 * - **A main-administrator right is locked both ways** (`ROLE_LOCKED`): a delegate may neither add nor remove it,
 *   whatever the role holds.
 * - **Anything else the delegate does not hold cannot be added** (`ROLE_BEYOND_YOURS`) - but removing a right is
 *   always allowed, so a right the role already holds stays switchable, and switching it back on restores what is
 *   stored rather than adding anything. That is why the stored set is part of the question.
 */
export function rightLock(key: string, context: LockContext): RightLock | null {
  if (context.mainAdmin) return null
  if (context.mainAdminGrants.has(key)) return 'main-admin-only'
  if (!context.grantable.has(key) && !context.stored.has(key)) return 'beyond-yours'
  return null
}

export function lockText(lock: RightLock): string {
  if (lock === 'main-admin-only') return tt('Prideľuje len hlavný administrátor')
  return tt('Toto právo nemáte, preto ho nemôžete pridať')
}

/**
 * *Zapnúť všetko* / *Vypnúť všetko* on one group, over the switches the reader may move.
 *
 * **A locked switch is neither counted nor moved.** Counting it would leave the button saying *Zapnúť všetko* for
 * ever after it has done all it may, and moving it would put a refused key into the proposal the server then rejects
 * whole.
 */
export function toggleGroup(
  granted: ReadonlySet<string>,
  keys: readonly string[],
  isLocked: (key: string) => boolean,
): { allOn: boolean; next: Set<string> } {
  const movable = keys.filter((key) => !isLocked(key))
  const allOn = movable.length > 0 && movable.every((key) => granted.has(key))
  const next = new Set(granted)
  for (const key of movable) {
    if (allOn) next.delete(key)
    else next.add(key)
  }
  return { allOn, next }
}

/** *Všetko · Len zapnuté · Len zmenené* - which rows the editor draws. */
export type RightsScope = 'all' | 'on' | 'changed'

export function inRightsScope(
  key: string,
  scope: RightsScope,
  granted: ReadonlySet<string>,
  stored: ReadonlySet<string>,
): boolean {
  if (scope === 'on') return granted.has(key)
  if (scope === 'changed') return granted.has(key) !== stored.has(key)
  return true
}

// ─── unsaved ticks, and what they were ticked against ────────────────────────

/**
 * One role's unsaved ticks - a whole replacement set, and the version of the role it replaces (§10A.408).
 *
 * **The set is a replacement, not a delta**: `PUT /settings/roles/{key}/permissions` replaces the role's rights, so a
 * set is right only against the rights it was ticked on. `basedOn` is the role's `updatedAt` at the first tick and
 * `baseRights` its rights then, so a save sends the version the ticks were made on rather than whatever the overview
 * read last - a re-read in between (the delete dialog's own read, a reconnect, a rename) must not turn somebody
 * else's save into one this save silently overwrites.
 */
export interface RoleTicks {
  set: ReadonlySet<string>
  basedOn: string | null
  /** The role's rights at the first tick, as `rightsKey` writes them. */
  baseRights: string
}

/** A set of rights as one comparable string - sorted, without repeats. */
export function rightsKey(permissions: Iterable<string>): string {
  return [...new Set(permissions)].sort().join(',')
}

/**
 * The ticks after one more change. **The base is the first tick's and stays it**, however many re-reads come between;
 * a proposal equal to what is stored now is no proposal (null), so the card stops saying *Neuložené zmeny* about a
 * role somebody ticked and unticked.
 */
export function nextTicks(
  previous: RoleTicks | undefined,
  next: ReadonlySet<string>,
  role: Pick<RoleView, 'updatedAt' | 'permissions'>,
): RoleTicks | null {
  if (rightsKey(next) === rightsKey(role.permissions)) return null
  return {
    set: next,
    basedOn: previous === undefined ? (role.updatedAt ?? null) : previous.basedOn,
    baseRights: previous === undefined ? rightsKey(role.permissions) : previous.baseRights,
  }
}

export interface TicksVersion {
  /** What the save sends as `expectedUpdatedAt`. */
  expectedUpdatedAt: string | null
  /** Somebody else's save moved the role's rights since the first tick. */
  rightsMoved: boolean
}

/**
 * Which version a save of these ticks sends, and whether the role's rights moved under them.
 *
 * - **Nothing changed since the first tick**: the version the ticks were made on.
 * - **The role changed and its rights did not** - a rename or a colour, since `updated_at` moves on every write of the
 *   row (§10A.407's trigger): the ticks still replace exactly what is stored, so the new version is sent.
 * - **The rights moved**: the first tick's version, which the server refuses with `ROLE_STALE` (409) - and the editor
 *   says so before the save is pressed, because the diff beside the switches is now against somebody else's save.
 */
export function ticksVersion(
  ticks: RoleTicks,
  role: Pick<RoleView, 'updatedAt' | 'permissions'>,
): TicksVersion {
  if (role.updatedAt === ticks.basedOn) return { expectedUpdatedAt: ticks.basedOn, rightsMoved: false }
  if (rightsKey(role.permissions) === ticks.baseRights) {
    return { expectedUpdatedAt: role.updatedAt ?? null, rightsMoved: false }
  }
  return { expectedUpdatedAt: ticks.basedOn, rightsMoved: true }
}

// ─── the preview rail ────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

/**
 * The server's preview, read rather than trusted.
 *
 * **The rail draws it beside the switches, so a shape the server did not send must not take the editor down**: an
 * older server, a proxy's error page or a stub that answers every write alike all arrive here as *something*. A
 * field that is not what §10A.408 says reads as empty, and `menuOnly` is true only when the server said so.
 */
export function previewOf(raw: unknown): RolePreview {
  const value = isRecord(raw) ? raw : {}
  const refused: RoleRefusedRight[] = (Array.isArray(value.refused) ? value.refused : [])
    .filter(isRecord)
    .filter((item) => typeof item.key === 'string')
    .map((item) => ({ key: item.key as string, code: typeof item.code === 'string' ? item.code : '' }))
  const affected: RoleAffectedPerson[] = (Array.isArray(value.affected) ? value.affected : [])
    .filter(isRecord)
    .filter((item) => typeof item.employeeId === 'number' && Number.isFinite(item.employeeId))
    .map((item) => ({
      employeeId: item.employeeId as number,
      name: typeof item.name === 'string' ? item.name : '',
      gains: stringsOf(item.gains),
      loses: stringsOf(item.loses),
    }))
  return {
    added: stringsOf(value.added),
    removed: stringsOf(value.removed),
    refused,
    affected,
    menuOnly: value.menuOnly === true,
  }
}

/**
 * *Zmení sa pre N ľudí. Uvidia to do troch minút.* - the confirmation's own sentence, from the server's preview.
 *
 * **The members' count is the fallback, not the figure**: the preview counts the people whose rights really move,
 * which a personal exception can make fewer than the members, and the overview's `memberCount` stands in only while
 * no preview has answered.
 *
 * **A preview naming nobody over a role that has people is not a role nobody has**: the server's plan lists only the
 * members whose set changes (`RoleRules.plan`), so `affected: []` with members means their exceptions already decide
 * every right the save moves - and saying *Rolu nikto nemá* there contradicts the rail beside it.
 */
export function savingReachText(affected: number | null, memberCount: number): string {
  if (affected === 0 && memberCount > 0) {
    return tt('Rolu majú ľudia, no nikomu z nich sa práva nezmenia - o týchto právach u nich rozhodujú výnimky.')
  }
  const count = affected ?? memberCount
  if (count <= 0) return tt('Rolu nikto nemá - zmena sa zatiaľ nikoho nedotkne.')
  // Accusative after *pre*: *pre 1 človeka*, *pre 3 ľudí* - not the cards' nominative *3 ľudia*.
  const whom = count === 1 ? tt('1 človeka') : `${formatCount(count)} ${tt('ľudí')}`
  return `${tt('Zmení sa pre')} ${whom}. ${tt('Uvidia to do troch minút.')}`
}

// ─── refusals ────────────────────────────────────────────────────────────────

/**
 * A refusal's code in words - for the places the server sends a code and no sentence.
 *
 * **The role picker's options and the preview's refused rights carry only a code** (§10A.408: the keys go in those
 * answers because only the client has labels for them), so this is where those codes are said. A refused *write*
 * carries the server's own sentence, and the screen shows that (`apiErrorMessage`) rather than this. O113's three are
 * said in the server's own words; an unknown code reads as a plain *not allowed* rather than as a reason invented for
 * it.
 */
export function refusalText(code: string | null | undefined): string {
  switch (code) {
    case 'ROLE_MAIN_ADMIN_ONLY':
      return tt('Toto smie len hlavný administrátor.')
    case 'ROLE_NOT_MANAGED':
      return tt('Túto rolu nespravujete.')
    case 'ROLE_OWN':
      return tt('Vlastnú rolu ani jej práva meniť nemôžete.')
    case 'ROLE_TOP':
      return tt('Hlavného administrátora a roly so základom Administrátor prideľuje len hlavný administrátor.')
    case 'ROLE_BEYOND_YOURS':
      return tt('Rola by tým dostala právo, ktoré nemáte ani vy.')
    case 'ROLE_LOCKED':
      return tt('Toto právo prideľuje len hlavný administrátor.')
    case 'ROLE_CYCLE':
      return tt('Spravovanie rolí nemôže ísť dokola.')
    case 'ROLE_HAS_MEMBERS':
      return tt('Rolu majú ľudia - najprv ich presuňte do inej roly.')
    case 'ROLE_STALE':
      return tt('Rolu medzitým niekto zmenil - načítajte ju znova.')
    case 'ROLE_NAME_TAKEN':
      return tt('Rola s týmto názvom už existuje.')
    case 'ROLE_NAME_BUILTIN':
      return tt('Tento názov patrí vstavanej role.')
    case 'ROLE_LIMIT':
      return tt('Vlastných rolí môže byť najviac 30.')
    case 'ROLE_INACTIVE_PERSON':
      return tt('Rolu možno prideliť len aktívnemu človeku.')
    case 'ROLE_UNKNOWN':
      return tt('Taká rola neexistuje.')
    // O113 (§10A.394) - the server's own sentences, so the picker and a refused save say one thing.
    case 'ONLY_A_MAIN_ADMIN_MAKES_ONE':
      return tt('Rolu hlavného administrátora môže prideliť len hlavný administrátor.')
    case 'MAIN_ADMIN_RECORD':
      return tt('Záznam iného hlavného administrátora upravovať nemôžete.')
    case 'LAST_MAIN_ADMIN':
      return tt('Toto je posledný hlavný administrátor - bez neho sa do systému nikto nedostane.')
    default:
      return tt('Túto rolu nemôžete prideliť.')
  }
}

// ─── a person's role ─────────────────────────────────────────────────────────

export type ConsequenceKind = 'signs-out' | 'open-orders' | 'server-stops' | 'gains' | 'loses'

export interface Consequence {
  kind: ConsequenceKind
  text: string
}

export interface ConsequenceContext {
  personName: string
  /** The base the person works as today, and the one the option would give them. */
  fromBase: string
  toBase: string
  serverChecked: ReadonlySet<string>
  labelOf: (key: string) => string
}

/**
 * What moving a person into one role would do, in the order it matters - design §2.9's person panel.
 *
 * 1. **A change of base signs them out** on every device (§10A.406, owner question #170), named with the two bases.
 * 2. **Their open orders**: the base decides whose wallet an unfinished order is in, so a change of base moves them.
 * 3. **What the server would stop allowing** - the rights they lose that the server checks; the rest of a loss is a
 *    menu entry. Said first among the losses because it is the half that turns a working shift into a refusal.
 * 4. Then everything they gain and lose, by label.
 */
export function optionConsequences(option: RoleOption, context: ConsequenceContext): Consequence[] {
  const said: Consequence[] = []
  if (option.signsOut) {
    said.push({
      kind: 'signs-out',
      text: `${tt('Mení sa základ z')} ${roleLabel(context.fromBase)} ${tt('na')} ${roleLabel(context.toBase)} - ${context.personName} ${tt('sa bude musieť znova prihlásiť na všetkých zariadeniach.')}`,
    })
  }
  if (option.openOrders > 0) {
    const tail = option.signsOut
      ? ` ${tt('Pri zmene základu sa zmení, ktoré z nich ukazuje jeho peňaženka.')}`
      : ''
    said.push({
      kind: 'open-orders',
      text: `${tt('Nedokončené objednávky na jeho mene:')} ${formatCount(option.openOrders)}.${tail}`,
    })
  }
  const stops = option.loses.filter((key) => context.serverChecked.has(key))
  if (stops.length > 0) {
    said.push({
      kind: 'server-stops',
      text: `${tt('Server prestane dovoľovať:')} ${stops.map(context.labelOf).join(', ')}`,
    })
  }
  if (option.gains.length > 0) {
    said.push({ kind: 'gains', text: `${tt('Získa:')} ${option.gains.map(context.labelOf).join(', ')}` })
  }
  if (option.loses.length > 0) {
    said.push({ kind: 'loses', text: `${tt('Stratí:')} ${option.loses.map(context.labelOf).join(', ')}` })
  }
  return said
}

// ─── the people list ─────────────────────────────────────────────────────────

export interface PeopleFilter {
  /** A role key, or `''` for every role. */
  role: string
  exceptionsOnly: boolean
  search: string
}

/**
 * People with exceptions first, then by name - the order the exceptions tab has had since legacy (`byOverridesThenName`),
 * kept because an exception is the thing on this list most worth finding.
 */
export function byExceptionsThenName(people: readonly RolePerson[]): RolePerson[] {
  return [...people].sort(
    (left, right) => right.overrideCount - left.overrideCount || left.name.localeCompare(right.name),
  )
}

/**
 * The people a filter leaves - §9.238's search, carried to *Ľudia*.
 *
 * **Name, position or role**, where §9.238 had name, e-mail or role: the overview sends no e-mail (§10A.408 lists the
 * people *without e-mail or phone*), and the position is what a manager remembers somebody by. The role matches by its
 * name as drawn and by its key, so *sofer*'s spirit holds: typing what the chip says finds everybody wearing it.
 * Diacritics are not folded, for §9.238's reason - one screen should not search two ways.
 */
export function peopleRows(
  people: readonly RolePerson[],
  filter: PeopleFilter,
  nameOf: (key: string) => string,
): RolePerson[] {
  const needle = filter.search.trim().toLowerCase()
  return byExceptionsThenName(
    people.filter((person) => {
      const key = effectiveRoleKey(person)
      if (filter.role !== '' && key !== filter.role) return false
      if (filter.exceptionsOnly && person.overrideCount <= 0) return false
      if (needle === '') return true
      return [person.name, person.position ?? '', nameOf(key), key].some((field) =>
        field.toLowerCase().includes(needle),
      )
    }),
  )
}

// ─── creating and deleting ───────────────────────────────────────────────────

export type RoleNameProblem = 'short' | 'long' | 'builtin' | 'taken'

/**
 * Why a custom role's name would be refused, before it is sent - §10A.408's three rules.
 *
 * 2 to 40 characters trimmed (V89's `staff_roles_shape_check`), not one of the five built-in labels (`ROLE_NAME_BUILTIN`,
 * held to `roleLabel` by the server's own test) and unique case-blind among the custom roles (`ROLE_NAME_TAKEN`). The
 * server decides; this only saves a round trip, so a rename keeps its own role out of the clash (`ignoreKey`).
 */
export function roleNameProblem(
  name: string,
  roles: readonly RoleView[],
  ignoreKey?: string,
): RoleNameProblem | null {
  const trimmed = name.trim()
  if (trimmed.length < 2) return 'short'
  if (trimmed.length > 40) return 'long'
  const folded = trimmed.toLowerCase()
  if (BUILT_IN_ORDER.some((key) => roleLabel(key).toLowerCase() === folded)) return 'builtin'
  const taken = roles.some(
    (role) =>
      !role.builtIn && role.key !== ignoreKey && (role.name ?? '').trim().toLowerCase() === folded,
  )
  return taken ? 'taken' : null
}

export function roleNameProblemText(problem: RoleNameProblem): string {
  if (problem === 'short') return tt('Názov musí mať aspoň 2 znaky.')
  if (problem === 'long') return tt('Názov môže mať najviac 40 znakov.')
  if (problem === 'builtin') return tt('Tento názov patrí vstavanej role.')
  return tt('Rola s týmto názvom už existuje.')
}

/**
 * Where a deleted role's people go by default - *Zmazať rolu*'s preselection (`DELETE /settings/roles/{key}?moveTo=`).
 *
 * The built-in role the custom one works as moves everybody and signs nobody out - **except the built-in
 * Administrátor**, whose pass the server grants on every unticked right it checks (owner question #58), while a custom
 * role based on Administrátor is held to its ticks (§10A.407, #162). Preselecting it would hand every member the pass,
 * by default and with nothing said. So an Administrátor-based role defaults to another custom role on the same base,
 * and when there is none, to no choice at all - the reader picks, and the dialog says what the built-in one would do.
 * A target the list does not hold is no default either, never the first role that happens to be listed.
 */
export function defaultMoveTarget(
  role: Pick<RoleView, 'key' | 'baseRole'>,
  targets: readonly Pick<RoleView, 'key' | 'baseRole' | 'builtIn'>[],
): string {
  if (role.baseRole === 'admin') {
    return (
      targets.find((target) => !target.builtIn && target.baseRole === 'admin' && target.key !== role.key)?.key ?? ''
    )
  }
  return targets.find((target) => target.key === role.baseRole)?.key ?? ''
}

/**
 * What a move into this role does beyond the base - the built-in Administrátor's pass (#58), said where it bites. Null
 * for any other target.
 */
export function moveTargetWarning(target: Pick<RoleView, 'administratorPass'> | undefined): string | null {
  if (target?.administratorPass !== true) return null
  return tt(
    'Vstavaný Administrátor prejde serverom aj na práva, ktoré nemá zaškrtnuté - presunutí ľudia budú smieť viac, než smeli v tejto role.',
  )
}

/**
 * How many of a deleted role's people a move signs out: all of them when the role they land in works as another base,
 * none when it is the same base (§10A.406 - only a change of base retires a token). A custom role's members all share
 * its base, which is what makes this a count rather than a list.
 */
export function signedOutByMove(
  role: Pick<RoleView, 'baseRole'>,
  target: Pick<RoleView, 'baseRole'> | undefined,
  members: number,
): number {
  if (target === undefined || members <= 0) return 0
  return target.baseRole === role.baseRole ? 0 : members
}

/** A custom role's name, colour and description as the rename form holds them. */
export interface RoleMetaDraft {
  name: string
  /** The colour picked, or null while none is - a role drawn in its base's colour has none stored. */
  color: RoleColor | null
  description: string
}

/**
 * What the rename form sends (`PATCH /settings/roles/{key}`): **only what the reader changed**.
 *
 * The server reads an absent field as *leave it* and a blank one as *clear it* (§10A.408), so a rename must not carry a
 * colour nobody chose - a role with none stored is drawn in its base's colour, and sending one would repaint its badge on
 * every screen - and clearing the description sends an empty string, because a null is read as *leave it*.
 */
export function roleMetaPatch(
  role: Pick<RoleView, 'name' | 'color' | 'description'>,
  draft: RoleMetaDraft,
): UpdateRoleRequest {
  const patch: UpdateRoleRequest = {}
  const name = draft.name.trim()
  if (name !== (role.name ?? '').trim()) patch.name = name
  const description = draft.description.trim()
  if (description !== (role.description ?? '').trim()) patch.description = description
  if (draft.color !== null && draft.color !== role.color) patch.color = draft.color
  return patch
}

interface OverrideLike {
  permission?: string
  granted?: boolean
}

/**
 * The personal grants a new role makes pointless - *Zrušiť výnimky, ktoré nová rola už obsahuje* (design §2.9).
 *
 * **Grants only.** A deny says *this person specifically may not*, and moving them into a role that holds the right
 * does not change that intention - removing the deny would hand them the right the main administrator took away.
 */
export function grantsTheRoleCovers(
  overrides: readonly OverrideLike[],
  rolePermissions: readonly string[],
): string[] {
  const held = new Set(rolePermissions)
  return overrides
    .filter((override) => override.granted === true && override.permission !== undefined)
    .map((override) => override.permission as string)
    .filter((permission) => held.has(permission))
}

/** The exceptions left once the covered grants are cleared - the body the existing exceptions endpoint takes. */
export function withoutCoveredGrants<T extends OverrideLike>(
  overrides: readonly T[],
  rolePermissions: readonly string[],
): T[] {
  const covered = new Set(grantsTheRoleCovers(overrides, rolePermissions))
  return overrides.filter(
    (override) => !(override.granted === true && covered.has(override.permission ?? '')),
  )
}
